import React from 'react'
import './index.css'
import Flex from 'components/Flex'
import logUtil from 'utils/log'
import useSelector from 'hooks/use-selector'
import { SQUAD_FILTER_TYPE } from 'modules/squad/redux/constants'
import loadMemberProductsByFilterTypeSquadAction from 'modules/squad/redux/actions/load-member-products-by-filter-type'
import useDispatch from 'hooks/use-dispatch'
import getMemberProductsByIdAndFilterTypeSquadSelector from 'modules/squad/redux/selectors/get-member-products-by-id-and-filter-type'
import FlexColumn from 'components/FlexColumn'
import pluralizeStringUtil from 'utils/string/plurialize'
import logEventAnalyticsUtil from 'utils/analytics/log-event'
import Button from 'components/Button'
import noopFunctionUtil from 'utils/functions/noop'
import Section from 'components/Section'
import areProductsLoadedByMemberIdSquadSelector from 'modules/squad/redux/selectors/are-products-loaded-by-member-id'
import H3 from 'components/H3'
import { SQUAD_MEMBER_SUPER_LIKED_PRODUCTS_ID } from 'modules/squad/constants'
import usePrevious from 'hooks/use-previous'

import ProductCollectionItem from 'containers/ProductCollection/components/Item'
import isArchivedProductVariantUtil from 'modules/product-variant/utils/is-archived/index.js'

const ANALYTICS_EVENT = {
  VIEW_SQUAD_FRIEND_SUPER_LIKED_PRODUCT: 'VIEW_SQUAD_FRIEND_SUPER_LIKED_PRODUCT',
  SEE_MORE_SQUAD_FRIEND_SUPER_LIKED_PRODUCT: 'SEE_MORE_SQUAD_FRIEND_SUPER_LIKED_PRODUCT',
  SHOW_SQUAD_SUPER_LIKED_SAMPLES: 'SHOW_SQUAD_SUPER_LIKED_SAMPLES',
  VIEW_SUPER_LIKED_PRODUCT: 'VIEW_SUPER_LIKED_PRODUCT',
  SEE_MORE_SQUAD_SUPER_LIKED_PRODUCT: 'SEE_MORE_SQUAD_SUPER_LIKED_PRODUCT'
}

const PAGE_SIZE = 8
const SKIP_COUNT = 0
const SEE_MORE = 'See more'
const LOADING = 'Loading...'

export default function SquadPageSuperLikedSamples (props) {
  const {
    name,
    id,
    isCurrentCustomer
  } = props
  const dispatch = useDispatch()
  const isLoaded = useSelector(
    areProductsLoadedByMemberIdSquadSelector(id, SQUAD_FILTER_TYPE.super_like)
  )
  /**
   * @type {import('modules/squad/types').GetSquadProductsResp}
   */
  const sampleProductsData = useSelector(getMemberProductsByIdAndFilterTypeSquadSelector(id, SQUAD_FILTER_TYPE.super_like))
  const products = sampleProductsData?.products
  const productsCount = products?.length
  const totalProductsCount = sampleProductsData?.total_product_count
  const hasMoreProducts = productsCount < totalProductsCount

  const debugProps = {
    isLoaded,
    sampleProductsData,
    totalProductsCount,
    hasMoreProducts
  }

  const prevProductsCount = usePrevious(productsCount)
  React.useEffect(() => {
    if (!prevProductsCount && productsCount) {
      logEventAnalyticsUtil(ANALYTICS_EVENT.SHOW_SQUAD_SUPER_LIKED_SAMPLES)
    }
  }, [
    prevProductsCount,
    productsCount
  ])

  const loadProducts = (params) => {
    dispatch(
      loadMemberProductsByFilterTypeSquadAction({
        memberId: id,
        filterType: SQUAD_FILTER_TYPE.super_like,
        pageSize: PAGE_SIZE,
        skipCount: SKIP_COUNT,
        ...params
      })
    )
  }

  React.useEffect(() => {
    if (id && !isLoaded) {
      loadProducts()
    }
  }, [id])

  const handleClickSeeMore = () => {
    logEventAnalyticsUtil(
      isCurrentCustomer
        ? ANALYTICS_EVENT.SEE_MORE_SQUAD_SUPER_LIKED_PRODUCT
        : ANALYTICS_EVENT.SEE_MORE_SQUAD_FRIEND_SUPER_LIKED_PRODUCT
    )
    loadProducts({
      skipCount: productsCount
    })
  }

  logUtil('SquadPageSuperLikedSamples').debug('debugProps=', debugProps)
  if (!isLoaded && !productsCount) {
    logUtil('SquadPageSuperLikedSamples').debug('[noop] not loaded')
    return <></>
  }

  if (!productsCount) {
    logUtil('SquadPageSuperLikedSamples').debug('[noop] no sampleProductsData')
    return <></>
  }

  return (
    <Section
      className='squad-super-liked-samples'
      id={SQUAD_MEMBER_SUPER_LIKED_PRODUCTS_ID}
    >
      <FlexColumn
        className='squad-super-liked-samples-content'
      >
        <Flex className='squad-super-liked-samples-title'>
          <svg
            width='32'
            height='32'
            viewBox='0 0 32 32'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M7.75493 11.3949C7.21687 11.3949 6.64513 11.5047 6.04731 11.7565C3.43523 12.8561 2.77373 15.6244 3.26449 17.7314C4.46797 22.9008 11.0396 25.8527 11.8672 26.2055C12.6948 25.8523 19.2667 22.897 20.4697 17.7314C20.9605 15.6244 20.299 12.8559 17.6867 11.7563C15.1408 10.6843 13.0656 12.1841 12.0624 13.7939C11.9784 13.9295 11.7565 13.9295 11.6718 13.7939C10.9039 12.5622 9.50847 11.3949 7.75493 11.3949Z'
              fill='#4439DF'
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M14.3129 6.15737C14.9108 5.90554 15.4825 5.79578 16.0206 5.79578C17.7741 5.79578 19.1695 6.96309 19.9374 8.1948C20.0221 8.33037 20.2441 8.33037 20.328 8.1948C21.3312 6.58499 23.4064 5.08517 25.9523 6.15714C28.5646 7.25677 29.2261 10.0253 28.7353 12.1322C27.8763 15.8208 24.2798 18.3824 21.992 19.6743C22.1959 19.2089 22.3625 18.729 22.4841 18.2355C23.0822 15.8079 22.1433 11.6747 18.9597 10.4078C15.9626 9.21474 12.7806 10.0807 11.4655 11.8128C11.1101 9.75367 11.8309 7.20224 14.3129 6.15737Z'
              fill='#9C99EE'
            />
          </svg>
          <H3>{isCurrentCustomer ? 'You’ve' : `${name} has`} superliked {totalProductsCount} {pluralizeStringUtil('sample', totalProductsCount)}</H3>
        </Flex>

        <Flex className='squad-super-liked-samples-list'>
          {
            sampleProductsData.products.map((sampleProduct, index) => {
              const key = `${sampleProduct.product_id}-${sampleProduct.variant_id}-${index}`

              // it is archived if online_store_url starts with a null value
              const isArchived = isArchivedProductVariantUtil(sampleProduct)

              return (
                <div
                  key={key}
                  className='product-collection-item-wrapper product'
                >
                  <ProductCollectionItem
                    productVariant={sampleProduct}
                    isArchived={isArchived}
                  />
                </div>

              )
            })
          }
        </Flex>

        {
          !!hasMoreProducts && (
            <Button
              className='squad-super-liked-samples-see-more'
              onClick={isLoaded ? handleClickSeeMore : noopFunctionUtil}
            >
              {isLoaded ? SEE_MORE : LOADING}
            </Button>
          )
        }
      </FlexColumn>
    </Section>
  )
}
